"use client"

import Iconfont from "@repo/ui/iconfont"
import { Picker } from "antd-mobile"
import { deleteCookie, getCookie, setCookie } from "cookies-next"
import { AnimatePresence, motion } from "framer-motion"
import { useLocale, useTranslations } from "next-intl"
import Link from "next/link"
import React, { CSSProperties, RefObject, useEffect, useMemo, useState } from "react"
import { Button, Col, Row, Space } from "antd"

import { HeaderUserInfo } from "../../../../interface"
import { MobileMenuState } from "../../utils/useMobileMenu"

import "../../index.scss"

import { GlobalOutlined } from "@ant-design/icons"
import {
  buildLocalePath,
  getPathSuffixWithoutLocale,
  getProxyAppName,
  getProxyLink,
  getSiteUrl,
  stripMountSegment,
} from "@repo/shared"

import { useSigninModal } from "../../../../../SignIn"
import { NavigationItem } from "../../../MainNavigation/type"
import { MetalMindLink } from "./_components/MetalMindLink"

const MOBILE_MENU_BASE_TOP = 48
const MOBILE_MEDIA_QUERY = "(max-width: 768px)"

type LanguageOption = {
  code: string
  label: string
  target?: string
}

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: "en", label: "English" },
  { code: "zh", label: "中文", target: "https://m.smm.cn" },
  { code: "ko", label: "한국어" },
  { code: "th", label: "แบบไทย" },
  { code: "id", label: "Bahasa" },
  { code: "vn", label: "Tiếng Việt" },
  { code: "fr", label: "Français" },
  { code: "ru", label: "Россия" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
]

const getLanguageLabel = (languageCode: string) => {
  return LANGUAGE_OPTIONS.find((option) => option.code === languageCode)?.label || "English"
}

interface MobileMenuProps {
  isOpen: boolean
  menuItems: NavigationItem[]
  userInfo: HeaderUserInfo | null
  mobileMenuState: MobileMenuState
  scrollContainerRef: RefObject<HTMLDivElement | null>
  currentUrl: string
}

/**
 * 移动端菜单组件（客户端组件）
 */
export const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  menuItems,
  userInfo,
  mobileMenuState,
  scrollContainerRef,
  currentUrl,
}) => {
  const {
    expandedMenuKey,
    selectedThirdLevelMenu,
    closeMobileMenu,
    toggleMenuExpansion,
    handleSecondLevelClick,
    handleBackToSecondLevel,
  } = mobileMenuState
  const locale = useLocale()
  const t = useTranslations("HeaderComp")

  const [announcementOffset, setAnnouncementOffset] = useState(0)
  const [isLanguagePopupOpen, setIsLanguagePopupOpen] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState<string>(locale)

  const memoMenu = useMemo<NavigationItem[]>(() => {
    if (getCookie(process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY || "")) {
      return [
        ...menuItems,
        {
          title: t("common.MyAccount"),
          href: "#",
          children: [
            {
              title: t("common.tabs.account"),
              href: getSiteUrl("user") + `/${locale}` + "/account",
            },
            {
              title: t("common.tabs.my-subscription"),
              href: getSiteUrl("user") + `/${locale}` + "/my-subscription",
            },
            {
              title: t("common.tabs.orderform"),
              href: getSiteUrl("user") + `/${locale}` + "/orderform",
            },
            {
              title: t("common.tabs.my-activities"),
              href: getSiteUrl("user") + `/${locale}` + "/my-activities",
            },
            {
              title: t("common.tabs.my-collection"),
              href: getSiteUrl("user") + `/${locale}` + "/my-collection",
            },
          ],
        },
        {
          title: "Metrix",
          href: getSiteUrl("ai"),
        },
      ]
    } else {
      return [
        ...menuItems,
        {
          title: "Metrix",
          href: getSiteUrl("ai"),
        },
      ]
    }
  }, [locale, menuItems, t])

  const loginUrl = useMemo(() => {
    const baseUrl = getSiteUrl("user") + "/" + locale
    const url = encodeURIComponent(currentUrl)
    return `${baseUrl}/login?url=${url}`
  }, [locale, currentUrl])

  useEffect(() => {
    if (!isOpen || typeof window === "undefined") {
      setAnnouncementOffset(0)
      return
    }

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY)
    let bannerEl: HTMLElement | null = null
    let resizeObserver: ResizeObserver | null = null

    const computeOffset = (target?: HTMLElement | null) => {
      const currentBanner = (target ?? bannerEl ?? document.querySelector(".announcementBanner")) as HTMLElement | null
      bannerEl = currentBanner
      const nextOffset =
        mediaQuery.matches && currentBanner ? Math.round(currentBanner.getBoundingClientRect().height) : 0
      setAnnouncementOffset((prev) => (prev === nextOffset ? prev : nextOffset))

      if (resizeObserver && currentBanner) {
        resizeObserver.observe(currentBanner)
      }
    }

    resizeObserver = new ResizeObserver(() => computeOffset())

    computeOffset()

    const mutationObserver = new MutationObserver(() => {
      const current = document.querySelector(".announcementBanner") as HTMLElement | null
      if (current !== bannerEl) {
        if (bannerEl && resizeObserver) {
          resizeObserver.unobserve(bannerEl)
        }
        bannerEl = current
        computeOffset(current)
      }
    })

    mutationObserver.observe(document.body, { childList: true, subtree: true })

    const handleMediaChange = () => computeOffset()
    mediaQuery.addEventListener("change", handleMediaChange)

    return () => {
      mediaQuery.removeEventListener("change", handleMediaChange)
      resizeObserver?.disconnect()
      mutationObserver.disconnect()
    }
  }, [isOpen])

  const mobileMenuTop = MOBILE_MENU_BASE_TOP + announcementOffset
  const menuPositionStyle: CSSProperties = useMemo(
    () => ({
      top: `${mobileMenuTop}px`,
      height: `calc(100dvh - ${mobileMenuTop}px)`,
    }),
    [mobileMenuTop]
  )
  const { showSignInModal } = useSigninModal()

  useEffect(() => {
    if (!isOpen) {
      setIsLanguagePopupOpen(false)
    }
  }, [isOpen])

  const saveLanguage = (nextLanguage: string) => {
    const targetLanguage = LANGUAGE_OPTIONS.find((option) => option.code === nextLanguage)
    if (nextLanguage !== "zh") {
      setCookie("locale", nextLanguage, {
        path: "/",
        sameSite: "lax",
        maxAge: 60 * 60 * 24 * 365,
      })
    }

    setIsLanguagePopupOpen(false)
    closeMobileMenu()

    if (!targetLanguage) return

    if (targetLanguage.target) {
      window.location.href = targetLanguage.target
      return
    }

    const deployAppName = getProxyAppName()
    const isDev = process.env.NEXT_PUBLIC_ENV === "development"

    let nextPathname: string
    if (deployAppName && !isDev) {
      const pathSuffix = stripMountSegment(getPathSuffixWithoutLocale(window.location.pathname), deployAppName)
      nextPathname = getProxyLink({
        locale: nextLanguage,
        path: pathSuffix.replace(/^\//, ""),
        appName: deployAppName,
        omitDefaultLocale: true,
      })
      if (window.location.search) {
        nextPathname += window.location.search
      }
      if (window.location.hash) {
        nextPathname += window.location.hash
      }
    } else {
      nextPathname =
        buildLocalePath(nextLanguage, window.location.pathname, {
          preserveQuery: true,
          preserveHash: true,
          queryString: window.location.search,
          hashString: window.location.hash,
          omitDefaultLocalePrefix: true,
        }) || "/"
    }

    const nextUrl = `${window.location.origin}${nextPathname}`
    window.location.href = nextUrl
  }

  /**
   * 处理退出登录
   * 参考 PC 端 UserProfile 组件的退出登录逻辑
   */
  const handleSignOut = () => {
    deleteCookie(process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY || "", {
      domain: ".metal.com",
      path: "/",
    })
    window.location.reload()
  }

  if (!isOpen) return null
  return (
    <>
      {typeof window !== "undefined" && (
        <div className="mobileMenuOverlay" style={menuPositionStyle} onClick={closeMobileMenu} />
      )}
      <div
        ref={scrollContainerRef}
        className="mobileMenuPanel"
        style={menuPositionStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mobileNavList">
          {memoMenu.map((menuItem, index) => {
            const menuTitle = typeof menuItem.title === "string" ? menuItem.title : `menu-item-${index}`
            const isExpanded = expandedMenuKey === menuTitle
            const shouldShowItem = !selectedThirdLevelMenu || selectedThirdLevelMenu.parentTitle === menuTitle
            const supportsThirdLevelMenu = menuItem.mobileMenu?.mode === "drilldown"
            if (menuTitle === "Metrix") {
              if (selectedThirdLevelMenu) {
                return null
              }
              return <MetalMindLink key={menuTitle} />
            }

            if (!shouldShowItem) {
              return null
            }

            return (
              <div key={menuTitle} className="mobileNavItem">
                {/* 一级菜单头部：当三级菜单显示时隐藏 */}
                {(!selectedThirdLevelMenu || selectedThirdLevelMenu.parentTitle !== menuTitle) && (
                  <div className="mobileNavItemHeader" onClick={() => toggleMenuExpansion(menuTitle)}>
                    <span>{menuItem.title}</span>
                    <Iconfont
                      type="icon-xiayijijiantou"
                      style={{
                        color: "#0c1019",
                        fontSize: 16,
                        transition: "transform 0.3s ease",
                        transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                      }}
                    />
                  </div>
                )}

                <AnimatePresence mode="wait">
                  {supportsThirdLevelMenu && isExpanded && !selectedThirdLevelMenu && (
                    <motion.div
                      className="mobileNavSubMenu"
                      key={expandedMenuKey}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{
                        height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                        opacity: { duration: 0.2, ease: "easeOut" },
                      }}
                      style={{ overflow: "hidden" }}
                    >
                      {menuItem.mobileMenu?.leadingLink && (
                        <Link
                          href={menuItem.mobileMenu.leadingLink.href}
                          className="mobileWatchlistLink"
                          onClick={closeMobileMenu}
                          {...(menuItem.mobileMenu.leadingLink.openInNewTab
                            ? { target: "_blank", rel: "noopener noreferrer" }
                            : {})}
                        >
                          <span>{menuItem.mobileMenu.leadingLink.title}</span>
                          <Iconfont
                            type="icon-a-Arrow-rightjiantouyou"
                            style={{ color: "#0C1019", fontSize: 14, marginLeft: 4 }}
                          />
                        </Link>
                      )}
                      <div className="mobileNavSubMenuContent">
                        {menuItem.children?.map((item: NavigationItem) => (
                          <div
                            key={item.title?.toString()}
                            className="mobileNavSubMenuItem"
                            onClick={() => handleSecondLevelClick(menuTitle, item)}
                            style={{ cursor: item.children && item.children.length > 0 ? "pointer" : "default" }}
                          >
                            {item.children && item.children.length > 0 ? (
                              <div className="mobileNavSubMenuLink">
                                {item.title}
                                {item.isHot && <Iconfont type="icon-huo" style={{ color: "#D7000F", fontSize: 12 }} />}
                                <Iconfont
                                  type="icon-xiayijijiantou"
                                  style={{ color: "#0C1019", fontSize: 16, marginLeft: "auto" }}
                                />
                              </div>
                            ) : (
                              <Link
                                href={item.href || "#"}
                                className="mobileNavSubMenuLink"
                                onClick={closeMobileMenu}
                                {...(item.openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                              >
                                {item.title}
                                {item.isHot && <Iconfont type="icon-huo" style={{ color: "#D7000F", fontSize: 12 }} />}
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {supportsThirdLevelMenu &&
                  isExpanded &&
                  selectedThirdLevelMenu &&
                  selectedThirdLevelMenu.parentTitle === menuTitle && (
                    <div className="mobileThirdLevelMenu">
                      <div className="mobileThirdLevelHeader">
                        <button className="mobileThirdLevelBackButton" onClick={handleBackToSecondLevel}>
                          <Iconfont
                            type="icon-xiayijijiantou"
                            style={{
                              color: "#0c1019",
                              fontSize: 16,
                              transform: "rotate(180deg)",
                            }}
                          />
                          <span>{t("common.Back")}</span>
                        </button>
                        <h2 className="mobileThirdLevelTitle">{selectedThirdLevelMenu.item.title}</h2>
                      </div>

                      <div className="mobileThirdLevelList">
                        {selectedThirdLevelMenu.item.children?.map((child, childIndex) => {
                          const key =
                            child.href && child.href !== "#"
                              ? child.href
                              : typeof child.title === "string"
                                ? `${child.title}-${childIndex}`
                                : `child-${childIndex}`

                          const href =
                            child.href && child.href !== "#"
                              ? child.href.startsWith("http")
                                ? child.href
                                : getSiteUrl("www") + "/" + locale + "/" + child.href
                              : "#"
                          return (
                            <Link
                              key={key}
                              href={href}
                              className={`mobileThirdLevelItem`}
                              onClick={closeMobileMenu}
                              {...(child.openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            >
                              {child.title}
                            </Link>
                          )
                        })}
                      </div>
                    </div>
                  )}

                <AnimatePresence>
                  {!supportsThirdLevelMenu && isExpanded && (
                    <motion.div
                      className="mobileNavSubMenuSecondary"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{
                        height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                        opacity: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                      }}
                      style={{ overflow: "hidden" }}
                    >
                      <div className="mobileNavSubMenuSecondaryContent">
                        {menuItem.children?.map((item: NavigationItem, itemIndex: number) => {
                          const subMenuTitle = typeof item.title === "string" ? item.title : `secondary-${itemIndex}`
                          const key = item.href && item.href !== "#" ? item.href : subMenuTitle

                          const href = item.href && item.href !== "#" ? item.href : "#"

                          return (
                            <Link
                              key={key}
                              href={href}
                              className="mobileNavSubMenuLinkSecondary"
                              onClick={closeMobileMenu}
                              {...(item.openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            >
                              {item.title}
                              <Iconfont
                                type="icon-xiayijijiantou"
                                style={{ color: "#0C1019", fontSize: 16, marginLeft: 4 }}
                              />
                            </Link>
                          )
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </div>
        {!selectedThirdLevelMenu && (
          <div className="mobileMenuFooter">
            {!userInfo ? (
              <>
                <Button
                  className="mobileSignInButton"
                  onClick={() => {
                    closeMobileMenu()
                    showSignInModal({
                      language: locale,
                      action: "login",
                      animate: "drawer",
                      onLoginSuccess: () => {
                        closeMobileMenu()
                        window.location.reload()
                      },
                      onRegisterSuccess: () => {
                        closeMobileMenu()
                        window.location.reload()
                      },
                    })
                  }}
                >
                  {t("common.SignIn")}
                </Button>

                <div className="mobileActionButtons">
                  <Link href={getSiteUrl("www") + "/" + locale + "/app"} className="mobileActionButton">
                    {t("common.DownloadApp")}
                  </Link>
                  <Link href={getSiteUrl("user") + "/" + locale + "/subscribe"} className="mobileActionButton">
                    {t("common.Subscribe")}
                  </Link>
                </div>
              </>
            ) : (
              <div className="mobileSignOutContainer">
                <Row>
                  <button className="mobileSignOutButton" onClick={handleSignOut}>
                    {t("common.SignOut")}
                  </button>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Link href={getSiteUrl("www") + "/" + locale + "/app"} className="mobileLink">
                      {t("common.DownloadApp")}
                    </Link>
                  </Col>
                  <Col span={12}>
                    <Link href={getSiteUrl("user") + "/" + locale + "/subscribe"} className="mobileLink">
                      {t("common.Subscribe")}
                    </Link>
                  </Col>
                </Row>
              </div>
            )}
            <div className="mobileFooterLinks">
              <Link
                href={getSiteUrl("www") + "/" + locale + "/announcements"}
                className="mobileFooterLink"
                onClick={closeMobileMenu}
              >
                {t("utilityNav.Announcements")}
              </Link>
              <Link
                href={getSiteUrl("www") + "/" + locale + "/about-us"}
                className="mobileFooterLink"
                onClick={closeMobileMenu}
              >
                {t("utilityNav.AboutUs")}
              </Link>
              <div className="mobileFooterLinksRight">
                <Link
                  href={getSiteUrl("www") + "/" + locale + "/careers"}
                  className="mobileFooterLink"
                  onClick={closeMobileMenu}
                >
                  {t("utilityNav.Careers")}
                </Link>
                <Space size={4} className="mobileFooterLink">
                  <GlobalOutlined />
                  <button
                    className="mobileLanguageSwitchTrigger"
                    onClick={(event) => {
                      event.stopPropagation()
                      setSelectedLanguage(locale)
                      setIsLanguagePopupOpen(true)
                    }}
                  >
                    {getLanguageLabel(locale)}
                  </button>
                </Space>
              </div>
            </div>
          </div>
        )}
      </div>
      <Picker
        visible={isLanguagePopupOpen}
        value={[selectedLanguage]}
        popupClassName="mobileLanguagePickerPopup"
        columns={[
          LANGUAGE_OPTIONS.map((option) => ({
            label: option.label,
            value: option.code,
          })),
        ]}
        title={<span style={{ fontWeight: 600 }}>{t("common.Language")}</span>}
        cancelText={t("common.Cancel")}
        confirmText={t("common.Save")}
        onSelect={(value) => setSelectedLanguage(String(value[0] ?? "en"))}
        onConfirm={(value) => saveLanguage(String(value[0] ?? "en"))}
        onClose={() => setIsLanguagePopupOpen(false)}
      />
    </>
  )
}
